import {
	expressAddClass,
	expressEventListener,
	expressQuerySelector,
	expressRemoveClass,
	getSelfOrParentWithClassName,
	hasClass,
} from "../../common/html";
import { addWishlistItem, deleteWishlistItem, IWishlistItem } from "../../services/wishlist-service";
import { getCookie, writeCookie } from "../../utils/cookie";
import { createTemp } from "../../services/authentication-service";
import { IDisposable } from "../../utils/disposable";

export interface IWishlistButton {
	readonly dispose: () => void;
	readonly setState: (liked: boolean) => void;
	readonly hasPredefinedProductNameAndPvc: (predefinedProductNameAndPvc: string) => boolean;
}

export interface ICreateWishListButtonOptions {
	readonly onLike?: (el: HTMLButtonElement) => void;
	readonly onDisLike?: (el: HTMLButtonElement) => void;
}
// Creates a wishlist button component
// This component requires the window.wishlist to be set on the page that initializes it
export function createWishListButton(
	el: HTMLButtonElement,
	opts?: ICreateWishListButtonOptions,
	addEventListeners: boolean = true,
) {
	const iconEl = expressQuerySelector<HTMLElement>(el, ".technical-icon-file", true);
	const iconUseEl = expressQuerySelector<HTMLElement>(iconEl, "use", true);
	const likedClass = "a-wishlist-button--liked";
	const hideClass = "u-hide";
	const likedIconPath = "/svg/heart-filled-symbol.svg#heart-filled-symbol";
	const dislikedIconPath = "/svg/heart-symbol.svg#heart-symbol";
	let executingOnClick = false;
	let disposable: IDisposable | undefined = undefined;
	// The productCategoryCode and predefinedProductNameAndPvc can be changed dynamically (dsp) so we read them on each click
	let productCategoryCode = el.dataset.productcategorycode || "";
	let predefinedProductNameAndPvc = el.dataset.predefinedproductnameandpvc || "";
	let theme = el.dataset.theme || "";

	function findWishlistItem() {
		return window.wishlist.find((item) => {
			const itemTheme = item.theme || "";
			const itemProductCategoryCode = item.productCategoryCode || "";
			const itemPredefinedProductNameAndPvc = item.predefinedProductNameAndPvc || "";
			return (
				itemProductCategoryCode.toLowerCase() === productCategoryCode.toLowerCase() &&
				itemPredefinedProductNameAndPvc.toLowerCase() === predefinedProductNameAndPvc.toLowerCase() &&
				itemTheme.toLowerCase() === theme.toLowerCase()
			);
		});
	}

	const onClickAction = (ev: MouseEvent) => {
		const el = getSelfOrParentWithClassName<HTMLButtonElement>(ev.target as HTMLElement, "technical-wishlist-button");
		productCategoryCode = el.dataset.productcategorycode;
		predefinedProductNameAndPvc = el.dataset.predefinedproductnameandpvc;
		theme = el.dataset.theme || "";
		if (!productCategoryCode && !predefinedProductNameAndPvc) return;
		const liked = hasClass(el, likedClass);
		if (liked) {
			deleteWishlistItem(productCategoryCode, predefinedProductNameAndPvc)
				.then((_) => {
					expressRemoveClass(el, likedClass);
					iconUseEl.setAttribute("href", dislikedIconPath);
					el.title = el.dataset.liketitle || "";

					if (window.wishlist) {
						const found = findWishlistItem();
						if (found) {
							const index = window.wishlist.indexOf(found);
							if (index >= 0) window.wishlist.splice(index, 1);
						}
					}
					if (opts && opts.onDisLike) opts.onDisLike(el);
					executingOnClick = false;
				})
				.catch(() => {
					executingOnClick = false;
				});
		} else {
			addWishlistItem(productCategoryCode, predefinedProductNameAndPvc, theme)
				.then((_) => {
					expressAddClass(el, likedClass);
					iconUseEl.setAttribute("href", likedIconPath);
					el.title = el.dataset.disliketitle || "";
					if (window.wishlist) {
						window.wishlist.push({ productCategoryCode, predefinedProductNameAndPvc, theme } as IWishlistItem);
					}
					if (opts && opts.onLike) opts.onLike(el);
					executingOnClick = false;
				})
				.catch(() => {
					executingOnClick = false;
				});
		}
	};

	const onClick = async (ev: MouseEvent) => {
		ev.preventDefault();
		if (executingOnClick) return;
		executingOnClick = true;
		const accId = getCookie("accid");
		const accType = getCookie("acctype");
		if (!accId) {
			const createTempResult = await createTemp();
			if (createTempResult.success) {
				if (accType !== createTempResult.accountType) writeCookie("acctype", createTempResult.accountType, 365);
				if (accId !== createTempResult.accountId) writeCookie("accid", createTempResult.accountId, 365);
				onClickAction(ev);
			}
		} else {
			onClickAction(ev);
		}
	};

	const setState = (liked: boolean) => {
		if (executingOnClick) return;
		executingOnClick = true;
		if (liked) {
			expressAddClass(el, likedClass);
			iconUseEl.setAttribute("href", likedIconPath);
			el.title = el.dataset.disliketitle || "";
		} else {
			expressRemoveClass(el, likedClass);
			iconUseEl.setAttribute("href", dislikedIconPath);
			el.title = el.dataset.liketitle || "";
		}
		executingOnClick = false;
	};

	// If addEventListeners == true (if false, the button is already created and it has a state) we check if the item is already in the wishlist and set the state accordingly
	if (addEventListeners) {
		disposable = expressEventListener(el, "click", onClick);
		// Early exit the element is not hidden (if it is not hidden it is set server side)
		if (hasClass(el, hideClass)) {
			// Check if the item is in the wishlist and set the state
			if (window.wishlist && window.wishlist.length > 0) {
				const found = findWishlistItem();
				found ? setState(true) : setState(false);
			} else {
				setState(false);
			}
			expressRemoveClass(el, hideClass);
		}
	}

	return {
		dispose: () => {
			disposable && disposable.dispose();
			expressAddClass(el, hideClass);
		},
		setState,
		hasPredefinedProductNameAndPvc: (predefinedProductNameAndPvc: string) => {
			return el.dataset.predefinedproductnameandpvc.toLowerCase() === predefinedProductNameAndPvc.toLowerCase();
		},
	};
}
